import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eraser, Pen } from 'lucide-react';

const SIGNATURE_FONTS = [
  { name: 'Great Vibes', family: "'Great Vibes', cursive" },
  { name: 'Allura', family: "'Allura', cursive" },
  { name: 'Dancing Script', family: "'Dancing Script', cursive" },
  { name: 'Sacramento', family: "'Sacramento', cursive" },
  { name: 'Parisienne', family: "'Parisienne', cursive" },
];

const LOISignaturePad = ({ onSignatureChange, onFontChange, initialFont, initialName, signatureSize = 100, hasError = false }) => {
  const [mode, setMode] = useState('type'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState(initialName || '');
  const initialNameApplied = useRef(false);
  const [selectedFont, setSelectedFont] = useState(
    SIGNATURE_FONTS.find(f => f.name === initialFont) || SIGNATURE_FONTS[0]
  );
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Store callback in ref to avoid re-triggering effects on every render
  const onSignatureChangeRef = useRef(onSignatureChange);
  useEffect(() => { onSignatureChangeRef.current = onSignatureChange; }, [onSignatureChange]);

  // Auto-fill typed signature from initialName (only once when it first becomes available)
  useEffect(() => {
    if (initialName && !initialNameApplied.current && !typedSignature && mode === 'type') {
      setTypedSignature(initialName);
      initialNameApplied.current = true;
    }
  }, [initialName, typedSignature, mode]);

  // Wait for Google Fonts to load
  useEffect(() => {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => setFontsLoaded(true));
    } else {
      const timer = setTimeout(() => setFontsLoaded(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#1f2937';
  }, [mode]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasDrawn) {
      exportSignature();
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignatureChangeRef.current && onSignatureChangeRef.current(null);
  };

  const exportSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSignatureChangeRef.current && onSignatureChangeRef.current(dataUrl);
  }, []);

  const handleFontSelect = (font) => {
    setSelectedFont(font);
    onFontChange && onFontChange(font.name);
  };

  // Generate typed signature as image using selected Google Font and size
  useEffect(() => {
    if (mode === 'type' && typedSignature.trim() && fontsLoaded) {
      const scale = (signatureSize || 100) / 100;
      const fontSize = Math.round(42 * scale);
      const canvasH = Math.round(120 * scale);
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 600, canvasH);
      ctx.font = `${fontSize}px ${selectedFont.family}`;
      ctx.fillStyle = '#1f2937';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, 20, canvasH / 2);
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChangeRef.current && onSignatureChangeRef.current(dataUrl);
    } else if (mode === 'type' && !typedSignature.trim()) {
      onSignatureChangeRef.current && onSignatureChangeRef.current(null);
    }
  }, [typedSignature, mode, selectedFont, fontsLoaded, signatureSize]);

  return (
    <div>
      {/* Mode tabs */}
      <div className="flex border-2 border-gray-200 rounded-lg overflow-hidden mb-3 w-fit">
        <button
          type="button"
          onClick={() => { setMode('draw'); setTypedSignature(''); }}
          className={`px-5 py-2 text-sm font-semibold flex items-center gap-2 transition-colors ${
            mode === 'draw' ? 'bg-[#2B4B6A] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Pen size={14} /> Draw
        </button>
        <button
          type="button"
          onClick={() => { setMode('type'); clearCanvas(); }}
          className={`px-5 py-2 text-sm font-semibold flex items-center gap-2 transition-colors ${
            mode === 'type' ? 'bg-[#2B4B6A] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          Type
        </button>
      </div>

      {mode === 'draw' ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            className={`w-full h-[120px] border-2 border-dashed rounded-xl cursor-crosshair touch-none ${
              hasError ? 'border-red-400 bg-red-50/50' : 'border-gray-200 bg-gray-50/50'
            }`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {!hasDrawn && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-gray-300 text-sm font-medium">Draw your signature here</span>
              <span className="text-gray-200 text-xs mt-1">Click and drag to sign</span>
            </div>
          )}
          {hasDrawn && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearCanvas}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              <Eraser size={16} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      ) : (
        <div>
          <Input
            value={typedSignature}
            onChange={(e) => setTypedSignature(e.target.value)}
            placeholder="Type your full name as signature"
            className={`text-2xl h-[60px] border-2 border-dashed ${
              hasError ? 'border-red-400 bg-red-50/50' : 'border-gray-200 bg-gray-50/50'
            }`}
            style={{ fontFamily: selectedFont.family }}
          />

          {/* Font selector */}
          <div className="mt-3">
            <p className="text-xs text-gray-500 mb-2">Choose signature style:</p>
            <div className="grid grid-cols-5 gap-2">
              {SIGNATURE_FONTS.map((font) => (
                <button
                  key={font.name}
                  type="button"
                  onClick={() => handleFontSelect(font)}
                  className={`px-2 py-3 rounded-lg border-2 transition-all text-center ${
                    selectedFont.name === font.name
                      ? 'border-[#2B4B6A] bg-blue-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <span
                    className="text-xl text-gray-800 block leading-tight"
                    style={{ fontFamily: font.family }}
                  >
                    {typedSignature || 'Signature'}
                  </span>
                  <span className="text-[9px] text-gray-400 mt-1 block">{font.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          {typedSignature && (
            <div className="mt-3 p-4 bg-white border border-gray-100 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Preview:</p>
              <p
                className="text-4xl text-gray-900"
                style={{ fontFamily: selectedFont.family }}
              >
                {typedSignature}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LOISignaturePad;
